import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from '@/services/axios'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const permissions = ref({})

  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  
  const isB2BCustomer = computed(() => {
    // B2B customers have clientType 'b2b' and various roles
    const clientType = user.value?.clientType?.toLowerCase()
    return clientType === 'b2b' && (user.value?.role === 'customer' || ['owner', 'manager', 'buyer', 'staff'].includes(user.value?.role))
  })
  
  const isSalesRep = computed(() => {
    const clientType = user.value?.clientType?.toLowerCase()
    return clientType === 'b2b' && ['sales_rep', 'manager'].includes(user.value?.role)
  })
  
  const isSuperAdmin = computed(() => {
    return user.value?.role === 'super_admin' || user.value?.role === 'admin' || user.value?.role === 'owner'
  })
  
  const canAccessSalesOrderEntry = computed(() => {
    // Allow sales reps, admins, and DTC employees with appropriate roles
    return isSalesRep.value || isSuperAdmin.value || 
           (user.value?.clientType === 'dtc' && ['manager', 'employee', 'admin'].includes(user.value?.role))
  })
  
  const canAccessManagement = computed(() => {
    // Allow sales reps, admins, and DTC managers
    return isSalesRep.value || isSuperAdmin.value || 
           (user.value?.clientType === 'dtc' && ['manager', 'admin'].includes(user.value?.role))
  })
  
  const userDisplayName = computed(() => {
    if (!user.value) return ''
    
    if (user.value.businessName) {
      return `${user.value.businessName} - ${user.value.firstName} ${user.value.lastName}`
    }
    
    return `${user.value.firstName} ${user.value.lastName}`
  })
  
  const creditInfo = computed(() => {
    if (!user.value?.creditInfo) return null
    
    return {
      status: user.value.creditInfo.status || 'pending',
      available: user.value.creditInfo.availableCredit || 0,
      limit: user.value.creditInfo.creditLimit || 0,
      outstanding: user.value.creditInfo.outstandingBalance || 0
    }
  })
  
    
  
  // Backward compatibility computed properties using unified permissions
  const canPlaceOrders = computed(() => hasPermission('b2b_orders.create'))
  const canViewPricing = computed(() => hasPermission('products.view'))
  const canViewInvoices = computed(() => hasPermission('orders.view'))
  const canManageAccount = computed(() => hasPermission('b2b_teams.manage'))
  const canViewReports = computed(() => hasPermission('reports.view'))
  const canManageUsers = computed(() => hasPermission('b2b_teams.manage'))
  // Permission helpers
  const hasPermission = (permission) => {
    if (!isAuthenticated.value) return false
    
    // Super admin/admin/owner bypass
    if (['admin', 'super_admin', 'owner'].includes(user.value?.role)) {
      return true
    }
    
    // Role-based permission mappings
    const rolePermissions = {
      manager: ['manage_inventory', 'view_reports', 'place_orders', 'view_pricing', 'view_invoices'],
      sales_rep: ['manage_clients', 'place_orders', 'view_pricing', 'view_reports', 'view_invoices', 'create_quotes'],
      buyer: ['place_orders', 'view_pricing', 'view_invoices'],
      viewer: ['view_reports', 'view_pricing']
    }
    
    // Check if the permission matches role-based permissions
    const userRole = user.value?.role
    if (userRole && rolePermissions[userRole]) {
      // Check if user has place_orders for order creation permissions
      if (permission === 'b2b_orders.create_for_client' && rolePermissions[userRole].includes('place_orders')) {
        return true
      }
      // Check if user has view_reports for various view permissions
      if (permission.includes('.view') && rolePermissions[userRole].includes('view_reports')) {
        return true
      }
    }
    
    // Check unified flat permission
    return permissions.value[permission] === true
  }
  
  const hasAllPermissions = (permissionList) => {
    return permissionList.every(p => hasPermission(p))
  }
  
  const hasAnyPermission = (permissionList) => {
    return permissionList.some(p => hasPermission(p))
  }
  
  const hasRole = (role) => {
    return user.value?.role === role
  }
  
  const hasAnyRole = (roles) => {
    return roles.includes(user.value?.role)
  }

  // Actions
  const login = async (credentials) => {
    loading.value = true
    error.value = null
    
    try {
      // Use the unified B2B auth endpoint
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/v1/b2b/auth/login`, {
        email: credentials.email,
        password: credentials.password
      })
      
      if (response.data.success) {
        const authToken = response.data.token
        const userData = response.data.user
        const organization = response.data.organization
        
        // Debug logging
        console.log('🔐 Login Response:', {
          userData,
          organization,
          hasRole: !!userData.role,
          role: userData.role
        })
        
        // Decode JWT to get user type
        const tokenPayload = JSON.parse(atob(authToken.split('.')[1]))
        
        // Enhance user data with organization info
        const enhancedUserData = {
          ...userData,
          clientType: tokenPayload.clientType || 'b2b', // Use actual clientType from JWT
          userType: tokenPayload.userType,
          organizationId: organization?.id,
          organizationName: organization?.name,
          organizationType: organization?.type,
          creditLimit: organization?.creditLimit,
          paymentTerms: organization?.paymentTerms,
          tier: organization?.tier
        }
        
        // Store auth data
        token.value = authToken
        user.value = enhancedUserData
        
        // Persist to localStorage first (axios interceptor will read from here)
        localStorage.setItem('b2b_token', authToken)
        localStorage.setItem('b2b_user', JSON.stringify(enhancedUserData))
        
        // Load permissions (axios interceptor will automatically add token)
        await loadPermissions()
        
        return { success: true }
      }
      
      return { success: false, error: 'Login failed' }
      
    } catch (err) {
      const message = err.response?.data?.error?.message || 
                     err.response?.data?.error || 
                     'Login failed'
      error.value = message
      return { success: false, error: message }
    } finally {
      loading.value = false
    }
  }
  
  const logout = async () => {
    try {
      // Call logout endpoint if token exists
      if (token.value) {
        await axios.post(`${import.meta.env.VITE_API_URL}/v1/b2b/auth/logout`)
      }
    } catch (err) {
      console.warn('Logout API call failed:', err)
    } finally {
      // Always clear local state
      user.value = null
      token.value = null
      error.value = null
      
      // Clear localStorage (axios interceptor will stop adding headers automatically)
      localStorage.removeItem('b2b_token')
      localStorage.removeItem('b2b_user')
      localStorage.removeItem('token') // Legacy fallback
      localStorage.removeItem('user') // Legacy fallback
    }
  }
  
  const initializeAuth = () => {
    // Try to restore authentication from localStorage
    const savedToken = localStorage.getItem('b2b_token') || localStorage.getItem('token')
    const savedUser = localStorage.getItem('b2b_user') || localStorage.getItem('user')
    
    if (savedToken && savedUser) {
      try {
        token.value = savedToken
        const savedUserData = JSON.parse(savedUser)
        
        // Decode JWT to get actual client type
        const tokenPayload = JSON.parse(atob(savedToken.split('.')[1]))
        let actualClientType = tokenPayload.clientType
        
        // Fallback: if clientType is missing but userType exists, infer clientType
        if (!actualClientType && tokenPayload.userType) {
          if (tokenPayload.userType === 'b2b_user') {
            actualClientType = 'b2b'
            console.log('Fallback: inferred clientType as "b2b" from userType')
          } else if (tokenPayload.userType === 'employee') {
            actualClientType = 'b2b' // Employees with B2B access use B2B portal
            console.log('Fallback: inferred clientType as "b2b" from employee userType')
          }
        }
        
        if (!actualClientType) {
          console.warn('Invalid saved token: missing client type and unable to infer')
          logout() // Clear corrupted state
          return false
        }
        
        // Ensure clientType is set from JWT token (in case it was missing from saved data)
        user.value = {
          ...savedUserData,
          clientType: actualClientType
        }
        
        // Token is already in localStorage, axios interceptor will handle headers automatically
        
        // Load cached permissions for internal users and B2B users
        if (actualClientType === 'internal' || actualClientType === 'admin' || actualClientType === 'b2b') {
          const cachedPermissions = localStorage.getItem('b2b_permissions')
          if (cachedPermissions) {
            try {
              permissions.value = JSON.parse(cachedPermissions)
            } catch (e) {
              permissions.value = {}
            }
          }
          // Refresh permissions in background
          loadPermissions()
        }
        
        return true
      } catch (err) {
        console.warn('Failed to restore auth state:', err)
        logout() // Clear corrupted state
        return false
      }
    }
    
    return false
  }
  
  const refreshToken = async () => {
    if (!token.value) return false
    
    try {
      loading.value = true
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/v1/b2b/auth/refresh`)
      
      const { token: newToken, user: userData } = response.data.data
      
      token.value = newToken
      user.value = userData
      
      // Update stored data
      localStorage.setItem('b2b_token', newToken)
      localStorage.setItem('b2b_user', JSON.stringify(userData))
      
      // Token updated in localStorage, axios interceptor will handle headers automatically
      
      return true
      
    } catch (err) {
      console.warn('Token refresh failed:', err)
      logout()
      return false
    } finally {
      loading.value = false
    }
  }
  
  const updateCreditInfo = (creditData) => {
    if (user.value) {
      user.value.creditInfo = { ...user.value.creditInfo, ...creditData }
      localStorage.setItem('b2b_user', JSON.stringify(user.value))
    }
  }
  
  const loadPermissions = async () => {
    try {
      // Use the B2B permissions endpoint
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/v1/b2b/auth/permissions`)
      
      if (response.data.success) {
        permissions.value = response.data.data?.permissions || response.data.permissions || {}
        localStorage.setItem('b2b_permissions', JSON.stringify(permissions.value))
      }
    } catch (err) {
      console.warn('Failed to load permissions:', err)
      // Try to load from cache
      const cached = localStorage.getItem('b2b_permissions')
      if (cached) {
        try {
          permissions.value = JSON.parse(cached)
        } catch (e) {
          permissions.value = {}
        }
      }
    }
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    permissions,
    
    // Computed
    isAuthenticated,
    isB2BCustomer,
    isSalesRep,
    isSuperAdmin,
    canAccessSalesOrderEntry,
    canAccessManagement,
    userDisplayName,
    creditInfo,
    
    // Backward compatibility
    canPlaceOrders,
    canViewPricing,
    canViewInvoices,
    canManageAccount,
    canViewReports,
    canManageUsers,
    
    // Permission methods
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    
    // Actions
    login,
    logout,
    initializeAuth,
    refreshToken,
    updateCreditInfo,
    loadPermissions
  }
})